import { Fragment, useRef, useState } from "react";
import axios from "../../../../axios";

// ПОЛЯ ЯПОНСКОГО ТУРА В АДМИНКЕ.
//
// Бэкенд умеет хранить конец тура, маршрут по городам, программу по дням,
// списки «включено / не включено», депозит и дополнительные опции —
// но в форме этих полей не было, и заполнить их можно было только запросом
// в API руками. Этот блок закрывает дыру: один компонент на обе формы,
// создание и редактирование.
//
// Наружу отдаёт patch — объект с изменившимися ключами:
//   onChange({ included: [...] })
// Форма делает setFormData((prev) => ({ ...prev, ...patch })).

const LANGS = [
  { code: "ru", label: "Рус" },
  { code: "en", label: "Eng" },
  { code: "kk", label: "Қаз" },
];

const INPUT =
  "w-full rounded-md border-0 py-1.5 px-3 text-neutral-900 ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6";
const ADD_BTN =
  "inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500";
const DEL_BTN =
  "inline-flex items-center justify-center h-8 w-8 shrink-0 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-500";

/** Заголовок раздела с пояснением, зачем поле нужно. */
const Section = ({ title, hint, action, children }) => (
  <section className="w-full rounded-lg border border-neutral-200 p-4">
    <div className="flex items-start justify-between gap-4">
      <div>
        <h3 className="text-base font-semibold text-neutral-800">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-neutral-500 max-w-[70ch]">{hint}</p>}
      </div>
      {action}
    </div>
    <div className="mt-3">{children}</div>
  </section>
);

/**
 * Одно фото — для дня программы. Загружает файл на тот же /upload, что и
 * галерея тура, и возвращает ссылку строкой. Нет фото — карточка дня на
 * сайте остаётся текстовой, серой заглушки не будет.
 */
const PhotoField = ({ value, onChange }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const { data } = await axios.post("/upload", fd);
      onChange(data.url);
    } catch (err) {
      console.warn("Не удалось загрузить фото дня:", err);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-3">
      {value ? (
        <img src={value} alt="" className="h-14 w-20 rounded object-cover" />
      ) : (
        <div className="flex h-14 w-20 items-center justify-center rounded border border-dashed border-neutral-300 text-xs text-neutral-400">
          нет фото
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={upload} />
      <button type="button" className={ADD_BTN} onClick={() => inputRef.current?.click()}>
        {busy ? "Загрузка…" : value ? "Заменить фото" : "Фото дня"}
      </button>
      {value && (
        <button type="button" className={DEL_BTN} onClick={() => onChange("")} title="Убрать фото">
          ✕
        </button>
      )}
    </div>
  );
};

/** Одна мультиязычная строка: четыре узких поля в ряд. */
const LangRow = ({ value = {}, onChange, placeholder = "", textarea = false }) => {
  const Cmp = textarea ? "textarea" : "input";
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 w-full">
      {LANGS.map((l) => (
        <label key={l.code} className="block">
          <span className="block text-xs text-neutral-400">{l.label}</span>
          <Cmp
            type="text"
            rows={textarea ? 2 : undefined}
            className={INPUT}
            placeholder={l.code === "ru" ? placeholder : ""}
            value={value?.[l.code] || ""}
            onChange={(e) => onChange({ ...value, [l.code]: e.target.value })}
          />
        </label>
      ))}
    </div>
  );
};

/**
 * Список мультиязычных строк: «включено», «не включено», города маршрута.
 * Пустые строки при сохранении отбрасывает форма — здесь их можно оставить.
 */
const LangList = ({ items = [], onChange, placeholder, addLabel }) => {
  const update = (i, next) => onChange(items.map((it, idx) => (idx === i ? next : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { ru: "", en: "", kk: "" }]);

  return (
    <div className="flex flex-col gap-3">
      {items.map((item, i) => (
        <div key={i} className="flex items-end gap-2">
          <div className="flex-1">
            <LangRow
              value={item}
              onChange={(next) => update(i, next)}
              placeholder={placeholder}
            />
          </div>
          <button type="button" className={DEL_BTN} onClick={() => remove(i)} title="Удалить строку">
            ✕
          </button>
        </div>
      ))}
      <div>
        <button type="button" className={ADD_BTN} onClick={add}>
          {addLabel}
        </button>
      </div>
    </div>
  );
};

/** Программа по дням: номер дня + заголовок + текст. */
const ItineraryList = ({ items = [], onChange }) => {
  const update = (i, patch) =>
    onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () =>
    onChange([
      ...items,
      { day: String(items.length + 1), title: {}, text: {} },
    ]);

  return (
    <div className="flex flex-col gap-4">
      {items.map((item, i) => (
        <div key={i} className="rounded-md bg-neutral-50 p-3">
          <div className="flex items-center gap-3">
            <label className="block w-24">
              <span className="block text-xs text-neutral-400">День</span>
              <input
                className={INPUT}
                value={item.day || ""}
                placeholder="1"
                onChange={(e) => update(i, { day: e.target.value })}
              />
            </label>
            <span className="mt-4 flex-1 text-xs text-neutral-400">
              Заголовок дня и описание — по-русски обязательно, остальные языки
              подтянутся по запасной цепочке.
            </span>
            <button type="button" className={`${DEL_BTN} mt-4`} onClick={() => remove(i)}>
              ✕
            </button>
          </div>
          <div className="mt-3">
            <LangRow
              value={item.title}
              onChange={(title) => update(i, { title })}
              placeholder="Токио — прибытие"
            />
          </div>
          <div className="mt-2">
            <LangRow
              textarea
              value={item.text}
              onChange={(text) => update(i, { text })}
              placeholder="Встреча в аэропорту, заселение, вечерняя прогулка"
            />
          </div>
          <div className="mt-2">
            <span className="block text-xs text-neutral-400">
              Город дня <span className="text-neutral-300">(показывается меткой над заголовком)</span>
            </span>
            <LangRow
              value={item.place}
              onChange={(place) => update(i, { place })}
              placeholder="Токио"
            />
          </div>
          <div className="mt-3">
            <PhotoField value={item.image} onChange={(image) => update(i, { image })} />
          </div>
        </div>
      ))}
      <div>
        <button type="button" className={ADD_BTN} onClick={add}>
          Добавить день
        </button>
      </div>
    </div>
  );
};

/**
 * ДОПОЛНИТЕЛЬНЫЕ ОПЦИИ.
 * Два вида: галочка (взял / не взял) и выбор одного варианта из нескольких.
 * Цена — строкой, как и везде: «150 $», «20 000 ₸». Пусто = «по запросу»,
 * «0» = бесплатно. Сайт сам вынет число и пересчитает итог.
 */
const ExtrasList = ({ items = [], onChange }) => {
  const update = (i, patch) =>
    onChange(items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = (type) =>
    onChange([
      ...items,
      type === "select"
        ? { type: "select", label: {}, note: {}, options: [{ label: {}, price: "" }] }
        : { type: "checkbox", label: {}, note: {}, price: "" },
    ]);

  const updateOption = (i, oi, patch) => {
    const options = (items[i].options || []).map((o, idx) =>
      idx === oi ? { ...o, ...patch } : o
    );
    update(i, { options });
  };

  return (
    <div className="flex flex-col gap-4">
      {items.map((item, i) => (
        <div key={i} className="rounded-md bg-neutral-50 p-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
              {item.type === "select" ? "Выбор варианта" : "Галочка"}
            </span>
            <button type="button" className={DEL_BTN} onClick={() => remove(i)}>
              ✕
            </button>
          </div>

          <div className="mt-2">
            <span className="block text-xs font-medium text-neutral-600">Название опции</span>
            <LangRow
              value={item.label}
              onChange={(label) => update(i, { label })}
              placeholder="Индивидуальный трансфер из аэропорта"
            />
          </div>

          <div className="mt-2">
            <span className="block text-xs font-medium text-neutral-600">
              Пояснение <span className="text-neutral-400">(необязательно)</span>
            </span>
            <LangRow
              value={item.note}
              onChange={(note) => update(i, { note })}
              placeholder="Машина бизнес-класса, до 3 человек с багажом"
            />
          </div>

          {item.type === "select" ? (
            <div className="mt-3">
              <span className="block text-xs font-medium text-neutral-600">Варианты</span>
              <div className="mt-1 flex flex-col gap-2">
                {(item.options || []).map((option, oi) => (
                  <Fragment key={oi}>
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <LangRow
                          value={option.label}
                          onChange={(label) => updateOption(i, oi, { label })}
                          placeholder="Отель 4★"
                        />
                      </div>
                      <label className="block w-32">
                        <span className="block text-xs text-neutral-400">Цена</span>
                        <input
                          className={INPUT}
                          value={option.price || ""}
                          placeholder="150 $"
                          onChange={(e) => updateOption(i, oi, { price: e.target.value })}
                        />
                      </label>
                      <button
                        type="button"
                        className={DEL_BTN}
                        onClick={() =>
                          update(i, {
                            options: item.options.filter((_, idx) => idx !== oi),
                          })
                        }
                      >
                        ✕
                      </button>
                    </div>
                  </Fragment>
                ))}
                <div>
                  <button
                    type="button"
                    className={ADD_BTN}
                    onClick={() =>
                      update(i, { options: [...(item.options || []), { label: {}, price: "" }] })
                    }
                  >
                    Добавить вариант
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <label className="mt-3 block w-40">
              <span className="block text-xs font-medium text-neutral-600">Цена</span>
              <input
                className={INPUT}
                value={item.price || ""}
                placeholder="150 $"
                onChange={(e) => update(i, { price: e.target.value })}
              />
            </label>
          )}
        </div>
      ))}

      <div className="flex gap-3">
        <button type="button" className={ADD_BTN} onClick={() => add("checkbox")}>
          Добавить галочку
        </button>
        <button type="button" className={ADD_BTN} onClick={() => add("select")}>
          Добавить выбор варианта
        </button>
      </div>
    </div>
  );
};

export default function TourFieldsInput({ value = {}, onChange }) {
  const set = (patch) => onChange(patch);

  return (
    <div className="w-full flex flex-col gap-4">
      <Section
        title="Даты и маршрут"
        hint="Конец тура показывает на карточке диапазон «18–25 ноября». Города маршрута сайт склеивает через тире: Токио – Фудзи – Киото."
      >
        <div className="flex flex-col gap-4">
          <label className="block max-w-xs">
            <span className="block text-xs font-medium text-neutral-600">
              Дата окончания <span className="text-neutral-400">(необязательно)</span>
            </span>
            <input
              type="date"
              className={INPUT}
              value={value.dateEnd || ""}
              onChange={(e) => set({ dateEnd: e.target.value })}
            />
          </label>
          <div>
            <span className="block text-xs font-medium text-neutral-600">Города маршрута</span>
            <div className="mt-1">
              <LangList
                items={value.route || []}
                onChange={(route) => set({ route })}
                placeholder="Токио"
                addLabel="Добавить город"
              />
            </div>
          </div>
        </div>
      </Section>

      <Section
        title="Цена и бронь"
        hint="Подпись к цене («на 1 человека») и взнос за место в группе. Взнос — главный ответ на страх «а вдруг не дадут визу»: остаток платится после визы."
      >
        <div className="flex flex-col gap-4">
          <div>
            <span className="block text-xs font-medium text-neutral-600">Подпись к цене</span>
            <LangRow
              value={value.priceNote}
              onChange={(priceNote) => set({ priceNote })}
              placeholder="на 1 человека"
            />
          </div>
          <label className="block max-w-xs">
            <span className="block text-xs font-medium text-neutral-600">Взнос за бронь</span>
            <input
              className={INPUT}
              value={value.deposit || ""}
              placeholder="75 000 ₸"
              onChange={(e) => set({ deposit: e.target.value })}
            />
          </label>
          <div>
            <span className="block text-xs font-medium text-neutral-600">Пояснение к взносу</span>
            <LangRow
              textarea
              value={value.depositNote}
              onChange={(depositNote) => set({ depositNote })}
              placeholder="Остаток оплачивается после получения визы"
            />
          </div>
        </div>
      </Section>

      <Section
        title="Программа по дням"
        hint="Каждый день — отдельная карточка на странице тура."
      >
        <ItineraryList
          items={value.itinerary || []}
          onChange={(itinerary) => set({ itinerary })}
        />
      </Section>

      <Section
        title="Включено в стоимость"
        hint="Список того, что человек уже оплатил."
      >
        <LangList
          items={value.included || []}
          onChange={(included) => set({ included })}
          placeholder="Проживание в отелях 3–4★ с завтраками"
          addLabel="Добавить пункт"
        />
      </Section>

      <Section
        title="Не включено"
        hint="Честный второй список. Он снимает половину вопросов менеджеру и все претензии на месте."
      >
        <LangList
          items={value.notIncluded || []}
          onChange={(notIncluded) => set({ notIncluded })}
          placeholder="Авиабилеты Астана — Токио"
          addLabel="Добавить пункт"
        />
      </Section>

      <Section
        title="Дополнительные опции"
        hint="То, что человек добирает сам: трансфер, улучшенный номер, экскурсии. На сайте это галочки с ценами и живым пересчётом итога — выбранное уходит вместе с заявкой. Цена строкой: «150 $», «0» — бесплатно, пусто — «по запросу»."
      >
        <ExtrasList items={value.extras || []} onChange={(extras) => set({ extras })} />
      </Section>

      <Section
        title="Кнопка тура"
        hint="Кнопка уводит в WhatsApp с готовым сообщением: название тура, даты, маршрут, цена, взнос и выбранные опции уже в тексте — клиенту остаётся нажать «отправить». Здесь настраивается только подпись."
      >
        <div>
          <span className="block text-xs font-medium text-neutral-600">
            Подпись кнопки <span className="text-neutral-400">(пусто — общая надпись)</span>
          </span>
          <LangRow
            value={value.ctaLabel}
            onChange={(ctaLabel) => set({ ctaLabel })}
            placeholder="Забронировать место"
          />
        </div>
      </Section>
    </div>
  );
}
